/**
 * Página de Monitoramento da Fila de Contratos PGMQ
 * 
 * Dashboard completo para observabilidade do pipeline de contratos
 * (geração PDF + envio para autenticadora), incluindo métricas,
 * gráficos de volume, alertas de saúde e tabela de itens.
 */

import { useState } from 'react';
import { RefreshCw, FileSignature, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useQueryClient } from '@tanstack/react-query';
import { ContractQueueStatusCards } from '@/components/dashboard/ContractQueueStatusCards';
import { ContractQueueVolumeChart } from '@/components/dashboard/ContractQueueVolumeChart';
import { ContractQueueHealthAlerts } from '@/components/dashboard/ContractQueueHealthAlerts';
import { ContractQueueItemsTable } from '@/components/dashboard/ContractQueueItemsTable';
import { ContractQueueSlowestItems } from '@/components/dashboard/ContractQueueSlowestItems';

export default function ContractQueueMonitor() {
  const queryClient = useQueryClient();
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Função para atualizar todos os dados
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['contract-queue-stats'] }),
      queryClient.invalidateQueries({ queryKey: ['contract-queue-items'] }),
      queryClient.invalidateQueries({ queryKey: ['contract-queue-volume'] }),
      queryClient.invalidateQueries({ queryKey: ['contract-queue-alerts'] }),
      queryClient.invalidateQueries({ queryKey: ['contract-queue-slowest'] }),
    ]);
    setLastUpdate(new Date());
    setIsRefreshing(false);
  };

  // Formatar tempo desde última atualização
  const getTimeSinceUpdate = () => {
    const seconds = Math.floor((new Date().getTime() - lastUpdate.getTime()) / 1000);
    if (seconds < 60) return `há ${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `há ${minutes}min`;
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileSignature className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Fila de Contratos</h1>
            <p className="text-muted-foreground">
              Monitoramento do pipeline de geração PDF e envio para autenticadora
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>Atualizado {getTimeSinceUpdate()}</span>
          </div>
          <Button onClick={handleRefresh} disabled={isRefreshing} variant="outline">
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Cards de Status */}
      <ContractQueueStatusCards />

      {/* Alertas de Saúde */}
      <ContractQueueHealthAlerts />

      {/* Gráfico de Volume */}
      <div className="grid gap-6 md:grid-cols-1">
        <ContractQueueVolumeChart />
      </div>

      {/* Top 20 mais lentos */}
      <ContractQueueSlowestItems />

      {/* Tabela de Itens */}
      <ContractQueueItemsTable />
    </div>
  );
}
